import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Loader2, History, X, Camera, CameraOff, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { landingLaunchBridge, voyageStatusBridge, bayTourBridge, voiceDisconnectBridge, voiceHandoffBridge } from "@/lib/voiceLaunchBridge";
import { useVoiceSession } from "@/context/VoiceSessionContext";
import { useCamera } from "@/hooks/useCamera";
import { toast } from "sonner";

// How often a fresh camera frame is pushed into the live conversation as an
// input_image item while Live Vision is on. 2.5s keeps AVA's view fresh while leaving
// the data channel plenty of headroom for audio-critical traffic (frames are now
// 1280px + detail:high for legible text, so they're bigger than they used to be).
const VISION_FRAME_INTERVAL_MS = 2500;

const STATUS_COLOR = {
  idle: "#00F0FF",
  connecting: "#FF9F1C",
  listening: "#00F0FF",
  speaking: "#FF9F1C",
  error: "#FF3366",
};

// Deep-tinted core color behind the orb per state - this is what makes the orb visibly
// "change colour" (not just glow brighter) between listening and speaking, per Doc's ask.
const STATUS_CORE = {
  idle: "#05070c",
  connecting: "#1a1206",
  listening: "#031a20",
  speaking: "#20130a",
  error: "#1a0508",
};

export default function RealtimeVoiceOrb() {
  const { activeConversationId, activeMode, notifyConversationCreated } = useVoiceSession();
  const [status, setStatus] = useState("closed"); // closed | connecting | listening | speaking | error
  const [linkHealth, setLinkHealth] = useState("idle"); // idle | good | degraded | reconnecting - surfaces network quality of the WebRTC leg to Doc via a small pill next to the orb
  const [caption, setCaption] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [visionEnabled, setVisionEnabled] = useState(true); // Doc's default: live camera on by default
  const [visionLive, setVisionLive] = useState(false); // true once a real frame is actively streaming
  const [matchChip, setMatchChip] = useState(null); // { label, confidence } - recognition chip over the cam window

  const pcRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioElRef = useRef(null);
  const dataChannelRef = useRef(null);
  const pendingUserTextRef = useRef("");
  const conversationIdRef = useRef(activeConversationId);
  const captionBufferRef = useRef("");
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const ringRef = useRef(null);
  const fleetRef = useRef([]);
  const recognitionAttemptedRef = useRef(false);
  const visionIntervalRef = useRef(null);
  const reconnectTimerRef = useRef(null); // pending "stayed-disconnected-too-long" watchdog
  const criticalTimerRef = useRef(null);  // hive-bus critical alert watcher
  const criticalCursorRef = useRef("");
  const announcedCriticalRef = useRef(new Set()); // ship|type combos already announced this session
  const autoRetryUsedRef = useRef(false); // one automatic reconnect per session before we hand it back to Doc
  const resumingRef = useRef(false); // true only during a silent in-session auto-reconnect - suppresses re-greeting/briefing
  const stableTimerRef = useRef(null); // re-arms one auto-retry once the link has been stable a while
  const assistantSavedRef = useRef(false); // ensures each turn's transcript is persisted exactly once
  // Voice-interaction telemetry -> the mothership's self-improving Voice Learning loop.
  // Pure, non-blocking measurement (no behaviour change): barge-ins, turn counts, session
  // length + reconnects are POSTed fire-and-forget so AVA's nightly review sees the Mothership too.
  const bargeInsRef = useRef(0);   // times Doc had to cut in while AVA was mid-response
  const avaTurnsRef = useRef(0);   // AVA responses this session
  const userTurnsRef = useRef(0);  // Doc utterances transcribed this session
  const sessionStartRef = useRef(0); // ms epoch of the current live session (0 = no session)
  const reconnectsRef = useRef(0);  // silent auto-reconnects this session
  const imageItemIdRef = useRef(null);
  const lastRecognizedFaceIdRef = useRef(null);
  const lastMemoryLabelRef = useRef(null);
  const lastRecognizedObjectRef = useRef(null);
  // Serialized proactive speech: only ONE response may be in flight, never while Doc is
  // mid-sentence. Overlapping response.create calls (greeting + face-match + tool output
  // colliding with a VAD-triggered turn) were the audible stutter/false-start "hiccups"
  // whenever AVA began speaking on her own initiative.
  const responseActiveRef = useRef(false);
  const userSpeakingRef = useRef(false);
  const pendingResponseRef = useRef(false);
  const matchChipTimerRef = useRef(null);
  const lastCuriosityAtRef = useRef(0);
  const frameCounterRef = useRef(0);
  const lastRecallAtRef = useRef(0);
  const lastRecallNoteRef = useRef("");
  const camera = useCamera();
  const micGateTimerRef = useRef(null);

  // Half-duplex mic gate (ported from the fleet orb): deafen the mic while AVA speaks so
  // her own voice out of the speakers is never re-heard as the user talking - the last
  // remaining self-start/self-interrupt vector on the mothership. The 250ms delay on
  // re-enable lets the speaker tail drain before the mic reopens.
  const setMicEnabled = useCallback((on) => {
    if (micGateTimerRef.current) { clearTimeout(micGateTimerRef.current); micGateTimerRef.current = null; }
    const stream = micStreamRef.current;
    if (!on) {
      if (stream) stream.getAudioTracks().forEach((t) => { t.enabled = false; });
      return;
    }
    micGateTimerRef.current = setTimeout(() => {
      const s = micStreamRef.current;
      if (s) s.getAudioTracks().forEach((t) => { t.enabled = true; });
    }, 250);
  }, []);

  // The single gate through which every self-initiated response is requested. If a
  // response is already active or Doc is speaking, the request is queued and fired
  // once on response.done - so AVA never talks over herself or over Doc.
  const requestResponse = useCallback((dc) => {
    const channel = dc || dataChannelRef.current;
    if (!channel || channel.readyState !== "open") return;
    if (responseActiveRef.current || userSpeakingRef.current) {
      pendingResponseRef.current = true;
      return;
    }
    responseActiveRef.current = true; // optimistic - response.created confirms
    channel.send(JSON.stringify({ type: "response.create" }));
  }, []);

  // Fire-and-forget interaction telemetry so the nightly Voice Learning engine sees the
  // Mothership's real metrics (barge-ins, turn counts, session length, reconnects), not
  // just the ships. Never blocks or affects the live session.
  const logVoiceEvent = useCallback((kind, meta) => {
    try { api.post("/v1/realtime/voice-event", { kind, meta: meta || {} }).catch(() => {}); } catch (e) {}
  }, []);
  const endSessionTelemetry = useCallback(() => {
    if (!sessionStartRef.current) return;
    logVoiceEvent("session_end", {
      duration_ms: Date.now() - sessionStartRef.current,
      ava_turns: avaTurnsRef.current,
      user_turns: userTurnsRef.current,
      barge_ins: bargeInsRef.current,
      reconnects: reconnectsRef.current,
    });
    sessionStartRef.current = 0;
    bargeInsRef.current = 0; avaTurnsRef.current = 0; userTurnsRef.current = 0; reconnectsRef.current = 0;
  }, [logVoiceEvent]);

  useEffect(() => {
    conversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const ensureConversationId = useCallback(async () => {
    if (conversationIdRef.current) return conversationIdRef.current;
    const res = await api.post("/conversations", { mode: activeMode });
    conversationIdRef.current = res.data.id;
    notifyConversationCreated(res.data.id);
    return res.data.id;
  }, [activeMode, notifyConversationCreated]);

  const fetchHistory = useCallback(async () => {
    if (!conversationIdRef.current) return;
    try {
      const res = await api.get(`/conversations/${conversationIdRef.current}/messages`);
      setHistory(res.data || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const saveExchange = useCallback(async (userText, assistantText) => {
    try {
      const cid = await ensureConversationId();
      await api.post(`/conversations/${cid}/voice-exchange`, { user_text: userText, assistant_text: assistantText });
      fetchHistory();
    } catch (e) {
      // non-fatal - voice keeps flowing even if persistence fails
    }
  }, [ensureConversationId, fetchHistory]);

  // Hard-navigate the user's browser to a URL, defeating every environment where a
  // naive window.location.href assignment silently fails or navigates the wrong frame:
  //
  //   1. If we're INSIDE AN IFRAME (Emergent preview shell embeds the mothership inside
  //      the dashboard), window.location.href only navigates the iframe - the top
  //      dashboard keeps showing the mothership. Doc's exact symptom: "AVA said it
  //      launched but I'm still staring at the mothership."
  //   2. Cross-origin window.top.location assignment throws SecurityError in many
  //      contexts, so we wrap it and fall through to strategy 3.
  //   3. A programmatically-clicked <a href target="_top"> is treated as a navigation
  //      request (not a scripted cross-origin location mutation) and IS permitted to
  //      break out of the iframe to the destination origin.
  //   4. As last resort, plain window.location.href - works when NOT iframed.
  //
  // Also emits a distinct console tag so Doc can verify from devtools which path fired.
  const hardNavigate = useCallback((url) => {
    if (!url) return false;
    console.info("[AVA voice-launch] hardNavigate →", url);
    // Strategy 3 first - most reliable across iframed + top-level scenarios.
    try {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_top";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      console.info("[AVA voice-launch] anchor _top click dispatched");
      return true;
    } catch (e) {
      console.warn("[AVA voice-launch] anchor click failed", e);
    }
    // Strategy 2 - window.top.location if we can reach it.
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = url;
        console.info("[AVA voice-launch] set window.top.location.href");
        return true;
      }
    } catch (e) {
      console.warn("[AVA voice-launch] window.top blocked (cross-origin frame)", e);
    }
    // Strategy 4 - plain location.href.
    try {
      window.location.href = url;
      console.info("[AVA voice-launch] set window.location.href");
      return true;
    } catch (e) {
      console.error("[AVA voice-launch] ALL nav strategies failed", e);
    }
    return false;
  }, []);

  const handleFunctionCall = useCallback((data) => {
    let callSucceeded = false;
    let output = { success: false, message: "Unknown project." };
    try {
      const args = JSON.parse(data.arguments || "{}");
      const rawQuery = (args.project_name || "").trim();
      const fleet = fleetRef.current;
      console.info("[AVA voice-launch] launch_project called with", rawQuery, "| fleet size:", fleet.length);

      // Robust project name matching. Doc calls projects by nicknames ("Health 360"
      // for "Octon Health EM"; "the drone one" for "OCTON Drone Latest"; "retina"
      // for "OCTON Retina") - strict substring matching drops the ball on every
      // one of these. We normalise (strip punctuation, drop "the" and the OCTON
      // brand prefix), tokenise both sides, and score by token overlap. Then rank.
      const stop = new Set(["octon", "the", "a", "an", "of", "and", "for", "to", "with"]);
      const norm = (s) => s.toLowerCase().replace(/[^\w\s]/g, " ")
        .split(/\s+/).filter(t => t && !stop.has(t));
      const queryTokens = norm(rawQuery);

      const scored = fleet.map((p) => {
        const nameTokens = norm(p.name);
        // Shared-token count with partial credit for token-substring matches: an
        // exact token hit is worth 1.0, a partial substring hit (e.g. "water"
        // inside "zerowater", or "diplo" inside "diplomatic") is worth 0.5. This
        // is what lets Doc's nicknames survive matching without needing every
        // project name to be a phrase.
        let shared = 0;
        for (const qt of queryTokens) {
          if (nameTokens.includes(qt)) shared += 1;
          else if (nameTokens.some(nt => nt.length >= 3 && qt.length >= 3 && (nt.includes(qt) || qt.includes(nt)))) shared += 0.5;
        }
        // Jaccard-ish score: shared / union - biases toward projects whose name
        // is entirely covered by the query rather than long names with 1 hit.
        const union = new Set([...queryTokens, ...nameTokens]).size || 1;
        const score = shared / union;
        // Bonus if the full raw query appears verbatim inside the project name
        // ("retina" inside "OCTON Retina"), or vice-versa. Catches simple cases
        // without depending on tokenisation.
        const rawL = rawQuery.toLowerCase();
        const nameL = p.name.toLowerCase();
        const substringBonus = (rawL && (nameL.includes(rawL) || rawL.includes(nameL))) ? 0.5 : 0;
        return { p, score: score + substringBonus, sharedTokens: shared };
      });

      // Exact-name check (short-circuit): if any project name matches the raw
      // query character-for-character (case-insensitive), use it and skip scoring.
      const exact = fleet.find((p) => p.name.toLowerCase() === rawQuery.toLowerCase());

      let match = exact || null;
      let ambiguousCandidates = [];
      if (!match) {
        scored.sort((a, b) => b.score - a.score || b.sharedTokens - a.sharedTokens);
        const best = scored[0];
        const second = scored[1];
        // Confident single match: high absolute score, or clearly ahead of #2.
        if (best && best.sharedTokens >= 1 && best.score >= 0.34 && (!second || best.score - second.score >= 0.15)) {
          match = best.p;
        } else if (best && best.sharedTokens > 0) {
          // Ambiguous - collect the top few candidates so AVA can ask Doc to disambiguate.
          ambiguousCandidates = scored.filter(s => s.sharedTokens > 0).slice(0, 3).map(s => s.p);
        }
      }

      if (match) {
        if (match.status !== "docked" || !match.url) {
          output = { success: false, message: `${match.name} is not docked yet - it has no live URL configured. Tell Doc to set one in the Docking Bay first.` };
        } else {
          // Guarantee navigation from the orb itself - decoupled from Landing's state so
          // React unmounts, closure staleness, or the WebRTC teardown cascade CANNOT
          // orphan the tab-change. Timer is scheduled BEFORE the bridge call, so it
          // runs regardless of what happens inside handleLaunch downstream.
          const targetUrl = match.url;
          callSucceeded = true;
          output = {
            success: true,
            message: `${match.name} undocking now. IMPORTANT: as your very next words, announce the handoff out loud in ONE short line - you are handing Doc over to ${match.name}'s own onboard voice, and you'll be right here when he returns to the mothership. One sentence only; the handoff happens seconds from now.`,
          };
          console.info("[AVA voice-launch] match →", match.name, "| url:", targetUrl, "| in iframe:", window.top !== window.self);
          // 3.4s instead of 1s: enough time for AVA to actually SPEAK the handoff line
          // before navigation tears the WebRTC session down.
          setTimeout(() => {
            console.info("[AVA voice-launch] timer fired, navigating");
            hardNavigate(targetUrl);
          }, 3400);
          if (landingLaunchBridge.current) {
            try {
              landingLaunchBridge.current(match, { viaVoice: true, skipNavigation: true });
            } catch (bridgeErr) {
              // Animation failed - cosmetic. Navigation timer above still fires.
            }
          }
        }
      } else if (ambiguousCandidates.length > 0) {
        // Multiple plausible interpretations - hand them back to AVA so she asks Doc
        // out loud "did you mean X, Y, or Z?" instead of silently doing nothing.
        const names = ambiguousCandidates.map(p => p.name).join(", ");
        output = { success: false, message: `Doc said "${rawQuery}" - that could match ${names}. Ask Doc which one he means, out loud.` };
      } else {
        // Nothing even close - surface the top 3 project names anyway so AVA can
        // offer alternatives instead of just saying "not found".
        const anySuggestions = fleet.slice(0, 5).map(p => p.name).join(", ");
        output = { success: false, message: `No project matches "${rawQuery}". Docked fleet includes: ${anySuggestions}. Ask Doc to clarify which he wants.` };
      }
    } catch (e) {
      output = { success: false, message: "Failed to parse launch request." };
    }

    if (callSucceeded) toast.success(output.message);
    const dc = dataChannelRef.current;
    if (dc && dc.readyState === "open") {
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    }
  }, [hardNavigate]);

  // Object Teach Shortcut: "remember this as my keys" mid-call - grabs the SAME frame the
  // live vision loop already has access to (no separate camera capture), teaches it via
  // /vision/teach, and reports back so AVA can confirm out loud whether it actually stuck.
  const handleTeachObjectCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let label = "";
    try {
      label = ((JSON.parse(data.arguments || "{}")).label || "").trim();
    } catch (e) {
      respond({ success: false, message: "Failed to parse the object label." });
      return;
    }
    if (!label) {
      respond({ success: false, message: "No label given to teach." });
      return;
    }
    const frame = camera.capture();
    if (!frame) {
      respond({ success: false, message: "Live Vision isn't on right now - ask Doc to enable the camera icon next to the voice orb first." });
      return;
    }
    api.post("/vision/teach", { image: frame, label }).then((res) => {
      toast.success(`AVA learned "${res.data.label}".`);
      respond({ success: true, message: `Learned "${res.data.label}" - recognized instantly next time it's in frame.` });
    }).catch(() => {
      respond({ success: false, message: "Couldn't process that frame for teaching - ask Doc to hold the object steadier and closer to the camera." });
    });
  }, [camera]);

  // Recognition chip: flashes the matched object's name over the live cam window the
  // instant AVA's vector memory identifies something, then fades out on its own (it keeps
  // refreshing for as long as the object stays in frame).
  const showMatchChip = useCallback((label, confidence) => {
    setMatchChip({ label, confidence });
    if (matchChipTimerRef.current) clearTimeout(matchChipTimerRef.current);
    matchChipTimerRef.current = setTimeout(() => setMatchChip(null), 4000);
  }, []);

  // "Do you remember this?" - checks the CURRENT live frame against AVA's own taught
  // 512-d object memory (learned_objects prototypes) and hands the verdict straight
  // back, so AVA answers from real recall instead of guessing from raw pixels.
  const handleRecognizeObjectCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    const frame = camera.capture();
    if (!frame) {
      respond({ recognized: false, message: "Live Vision isn't on right now - ask Doc to enable the camera icon next to the voice orb first." });
      return;
    }
    api.post("/vision/recognize", { image: frame }).then((res) => {
      if (res.data.recognized) {
        lastRecognizedObjectRef.current = res.data.label;
        showMatchChip(res.data.label, res.data.confidence);
      }
      respond(res.data);
    }).catch(() => {
      respond({ recognized: false, message: "The recognition check failed - try once more." });
    });
  }, [camera, showMatchChip]);

  // "What objects do you know?" - reads AVA's real trained library (Doc-taught AND
  // self-learned) so the spoken answer always matches what's actually in memory.
  const handleListObjectsCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    api.get("/vision/objects").then((res) => {
      const objects = (res.data || []).map((o) => ({
        label: o.label,
        training_angles: o.sample_count || 1,
        learned_by: o.self_taught ? "self-learned autonomously" : "taught by Doc",
        times_seen: o.sighting_count || 1,
      }));
      respond({ count: objects.length, objects });
    }).catch(() => {
      respond({ count: 0, objects: [], message: "Couldn't read the visual memory library right now - try again in a moment." });
    });
  }, []);

  // Voice batch review: AVA reads the pending queue aloud; Doc gives verdicts per item.
  const handleListReviewCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    api.get("/vision/review").then((res) => {
      const items = (res.data.pending || []).map((p) => ({
        item_id: p.id,
        proposed_label: p.proposed_label,
        confidence: p.confidence,
        detector: p.detector,
      }));
      respond({
        count: items.length,
        items,
        note: items.length
          ? "Read these to Doc one at a time (name + confidence) and collect an approve/reject verdict for each, then call resolve_review_item per verdict."
          : "The review queue is empty - nothing awaiting Doc's verdict.",
      });
    }).catch(() => {
      respond({ count: 0, items: [], message: "Couldn't read the review queue right now - try again shortly." });
    });
  }, []);

  const handleResolveReviewCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let args = {};
    try { args = JSON.parse(data.arguments || "{}"); } catch { args = {}; }
    if (!args.item_id || !["approve", "reject"].includes(args.action)) {
      respond({ ok: false, message: "Need item_id plus action 'approve' or 'reject'." });
      return;
    }
    const call = args.action === "approve"
      ? api.post(`/vision/review/${args.item_id}/approve`, { label: (args.label || "").trim() })
      : api.post(`/vision/review/${args.item_id}/reject`);
    call.then((res) => {
      if (args.action === "approve") toast.success(`AVA learned "${res.data.label}" - approved by voice.`);
      else toast.success("Rejected by voice - retained as a hard-negative.");
      respond({ ok: true, ...res.data });
    }).catch((e) => {
      respond({ ok: false, message: e?.response?.data?.detail || "That verdict failed - the item may already be resolved." });
    });
  }, []);

  // "Show me my fleet" - kicks off the bay-tour spotlight on the mothership and hands
  // AVA the ordered roster so her narration tracks the moving light.
  const handleFleetTourCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    const startTour = bayTourBridge.current;
    if (!startTour) {
      respond({ started: false, message: "The bay tour plays on the mothership page - Doc isn't there right now. Ask him to head back to the mothership first." });
      return;
    }
    const roster = startTour();
    respond({
      started: true,
      ships: roster,
      note: "The spotlight is sweeping the bays NOW at ~2.5 seconds per ship, in this exact order. Narrate one short ceremonial line per ship, keeping pace.",
    });
  }, []);

  // "Which ships are out?" - real live voyage state from the mothership page's bridge.
  const handleSleepReportCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    api.get("/brain/sleep-report")
      .then((res) => respond(res.data))
      .catch(() => respond({ ok: false, note: "The sleep-report lookup failed - say you couldn't reach your cycle logs just now." }));
  }, []);

  const handleFleetStatusCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    const getStatus = voyageStatusBridge.current;
    if (!getStatus) {
      respond({ ships_out: [], count: 0, note: "Voyage tracking lives on the mothership page and Doc isn't there right now - no orbiting ships are being tracked from here." });
      return;
    }
    const out = getStatus();
    respond({
      ships_out: out,
      count: out.length,
      note: out.length
        ? "These projects' tabs are open right now - their ships are orbiting the mothership."
        : "Every ship is docked in its bay - no projects launched from the mothership are open right now.",
    });
  }, []);

  // MANDATORY fact-grounding: the model calls web_search before stating any real-world
  // fact; we run it through the backend's Brave grounding endpoint and hand back results
  // it must answer FROM - or an explicit "couldn't verify" directive when search fails.
  // Live line to the OCTON retinal ship - answers "what did my latest scan show?" etc.
  const handleOctonBridgeCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let args = {};
    try { args = JSON.parse(data.arguments || "{}"); } catch (e) { /* empty */ }
    api.post("/bridge/action", { action: args.action || "latest-scan", text: args.text || "" })
      .then((res) => respond(res.data))
      .catch(() => respond({ ok: false, message: "The OCTON bridge didn't answer - say the retinal ship link failed just now." }));
  }, [requestResponse]);

  const handleShipDataCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let args = {};
    try { args = JSON.parse(data.arguments || "{}"); } catch (e) { /* empty */ }
    api.post("/bridge/ship-data", { ship: args.ship || "", collection: args.collection || null, query: args.query || null, limit: args.limit || 10 })
      .then((res) => respond(res.data))
      .catch(() => respond({ ok: false, message: "The fleet data bridge didn't answer - say the ship's data link failed just now." }));
  }, [requestResponse]);

  const handleWebSearchCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let query = "";
    try { query = ((JSON.parse(data.arguments || "{}")).query || "").trim(); } catch { query = ""; }
    if (!query) {
      respond({ ok: false, results: [], message: "No search query provided - form a focused query and call web_search again." });
      return;
    }
    api.post("/v1/realtime/web-search", { query }).then((res) => {
      respond(res.data);
    }).catch(() => {
      respond({ ok: false, results: [], message: "Web search failed - tell Doc plainly you couldn't verify this. Do NOT answer from training memory." });
    });
  }, []);

  // Real published-literature grounding - PubMed + CrossRef. AVA calls this for any
  // substantive medical claim so citations come from actual peer-reviewed journals with
  // real PMIDs, not synthesized-sounding source labels.
  const handleSearchJournalsCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let query = "";
    let includeFulltext = false;
    try {
      const args = JSON.parse(data.arguments || "{}");
      query = (args.query || "").trim();
      includeFulltext = !!args.include_fulltext;
    } catch { query = ""; }
    if (!query) {
      respond({ ok: false, results: [], message: "No query provided - form a focused biomedical query and call search_journals again." });
      return;
    }
    api.post("/v1/realtime/search-journals", { query, include_fulltext: includeFulltext }).then((res) => {
      respond(res.data);
    }).catch(() => {
      respond({ ok: false, results: [], message: "Journal search failed - flag the absence of a citation and reason from consolidated medical training, tiered honestly." });
    });
  }, []);

  // Deep OCR: full-res frame through Claude vision for an exact transcription - the
  // fix for AVA misreading simple writing off the lower-res live stream frames.
  const handleReadTextCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    const frame = camera.capture();
    if (!frame) {
      respond({ text: "", legible: false, message: "Live Vision isn't on right now - ask Doc to enable the camera icon next to the voice orb first." });
      return;
    }
    api.post("/vision/read-text", { image: frame }).then((res) => {
      respond(res.data);
    }).catch(() => {
      respond({ text: "", legible: false, message: "The deep read failed - ask Doc to hold the text closer and steadier, then call read_text once more." });
    });
  }, [camera]);

  // Long-term voice recall, proactive path: the moment Doc's utterance transcript lands,
  // silently search permanent semantic memory (every past text + voice session) and
  // inject matches as hidden context - NO response.create, never interrupts; AVA weaves
  // recalled history into her next natural turn. Throttled to one lookup per 15s.
  const attemptSemanticRecall = useCallback((transcript) => {
    const q = (transcript || "").trim();
    if (q.length < 12) return;
    if (Date.now() - lastRecallAtRef.current < 15000) return;
    lastRecallAtRef.current = Date.now();
    api.post("/v1/realtime/recall", { query: q, conversation_id: conversationIdRef.current }).then((res) => {
      if (!res.data.ok || !res.data.memories) return;
      const head = res.data.memories.slice(0, 160);
      if (head === lastRecallNoteRef.current) return; // identical memories already in context
      lastRecallNoteRef.current = head;
      const dc = dataChannelRef.current;
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "system",
          content: [{
            type: "input_text",
            text: `[LONG-TERM RECALL - your permanent memory matched Doc's words to these real past exchanges. Weave them in naturally, cite the date when relevant, never ask him to re-explain what's already here:]\n${res.data.memories}`,
          }],
        },
      }));
    }).catch(() => {}); // non-fatal - voice flows fine without recall
  }, []);

  // Long-term voice recall, explicit path: the recall_memory function tool - AVA calls
  // this before ever claiming she doesn't remember a past discussion.
  const handleRecallMemoryCall = useCallback((data) => {
    const dc = dataChannelRef.current;
    const respond = (output) => {
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: data.call_id, output: JSON.stringify(output) },
      }));
      requestResponse(dc);
    };
    let query = "";
    let project = "";
    try {
      const parsed = JSON.parse(data.arguments || "{}");
      query = (parsed.query || "").trim();
      project = (parsed.project || "").trim();
    } catch (e) { /* falls through to the empty-query guard */ }
    if (!query) {
      respond({ ok: false, message: "No query given for memory recall." });
      return;
    }
    api.post("/v1/realtime/recall", { query, project: project || null, conversation_id: conversationIdRef.current })
      .then((res) => respond(res.data))
      .catch(() => respond({ ok: false, message: "Memory recall failed - say plainly you couldn't reach your long-term memory just now." }));
  }, []);

  const handleDataChannelMessage = useCallback((event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      return;
    }
    switch (data.type) {
      case "input_audio_buffer.speech_started":
        userSpeakingRef.current = true;
        // Doc speaking WHILE AVA is mid-response = a barge-in (the "interrupting" signal).
        if (responseActiveRef.current) { bargeInsRef.current += 1; logVoiceEvent("barge_in", {}); }
        setStatus("listening");
        setCaption("");
        captionBufferRef.current = "";
        break;
      case "input_audio_buffer.speech_stopped":
        userSpeakingRef.current = false;
        break;
      case "conversation.item.input_audio_transcription.completed":
        pendingUserTextRef.current = data.transcript || "";
        userTurnsRef.current += 1;
        attemptSemanticRecall(data.transcript);
        break;
      case "response.created":
        responseActiveRef.current = true;
        avaTurnsRef.current += 1;
        // A newly created response already sees every queued tool output in the
        // conversation, so a separately queued follow-up would just double-speak.
        // (Clearing here, not on speech_started, so tool answers are never dropped.)
        pendingResponseRef.current = false;
        assistantSavedRef.current = false; // new turn - allow exactly one transcript save for it
        setMicEnabled(false);
        setStatus("speaking");
        captionBufferRef.current = "";
        break;
      case "output_audio_buffer.started":
        setMicEnabled(false);
        setStatus("speaking");
        break;
      case "output_audio_buffer.stopped":
        setMicEnabled(true);
        break;
      case "response.audio_transcript.delta":
      case "response.output_audio_transcript.delta": // GA / gpt-realtime-2.1 renamed this event
        captionBufferRef.current += data.delta || "";
        setCaption(captionBufferRef.current);
        break;
      case "response.audio_transcript.done":
      case "response.output_audio_transcript.done": // GA / gpt-realtime-2.1 renamed this event
        if (!assistantSavedRef.current) {
          const _at = data.transcript || captionBufferRef.current;
          if (pendingUserTextRef.current || _at) {
            saveExchange(pendingUserTextRef.current, _at);
            assistantSavedRef.current = true;
          }
        }
        pendingUserTextRef.current = "";
        break;
      case "response.function_call_arguments.done":
        if (data.name === "launch_project") handleFunctionCall(data);
        else if (data.name === "web_search") handleWebSearchCall(data);
        else if (data.name === "search_journals") handleSearchJournalsCall(data);
        else if (data.name === "read_text") handleReadTextCall(data);
        else if (data.name === "teach_object") handleTeachObjectCall(data);
        else if (data.name === "recognize_object") handleRecognizeObjectCall(data);
        else if (data.name === "list_known_objects") handleListObjectsCall(data);
        else if (data.name === "fleet_status") handleFleetStatusCall(data);
        else if (data.name === "sleep_report") handleSleepReportCall(data);
        else if (data.name === "list_review_queue") handleListReviewCall(data);
        else if (data.name === "resolve_review_item") handleResolveReviewCall(data);
        else if (data.name === "fleet_tour") handleFleetTourCall(data);
        else if (data.name === "recall_memory") handleRecallMemoryCall(data);
        else if (data.name === "octon_retina_bridge") handleOctonBridgeCall(data);
        else if (data.name === "ship_data") handleShipDataCall(data);
        break;
      case "response.done":
        responseActiveRef.current = false;
        setMicEnabled(true);
        // Schema-proof safety net: if no transcript-done event persisted this turn (realtime
        // API event names drift between versions), pull the final transcript straight from the
        // response payload and save it here so conversational memory is NEVER silently lost.
        if (!assistantSavedRef.current) {
          let _at = "";
          try {
            const _out = (data.response && data.response.output) || [];
            for (const _it of _out) {
              for (const _c of (_it.content || [])) {
                if (_c.transcript) { _at = _c.transcript; break; }
                if (_c.type === "text" && _c.text) { _at = _c.text; break; }
              }
              if (_at) break;
            }
          } catch (e) { /* fall through to caption */ }
          if (!_at) _at = captionBufferRef.current;
          if (pendingUserTextRef.current || _at) {
            saveExchange(pendingUserTextRef.current, _at);
            assistantSavedRef.current = true;
          }
          pendingUserTextRef.current = "";
        }
        if (pendingResponseRef.current && !userSpeakingRef.current) {
          pendingResponseRef.current = false;
          // brief settle so the previous utterance's audio tail fully drains first
          setTimeout(() => requestResponse(), 300);
        }
        setStatus("listening");
        break;
      case "error":
        // A rejected event must never wedge the speech gate shut (self-start loop guard).
        responseActiveRef.current = false;
        pendingResponseRef.current = false;
        break;
      case "conversation.item.created":
        // Track the server-assigned id of our own live-camera image item so the NEXT
        // frame can delete this one first - keeps exactly one (the freshest) frame in
        // context at all times instead of endlessly bloating the conversation history.
        if (data.item?.content?.[0]?.type === "input_image") {
          imageItemIdRef.current = data.item.id;
        }
        break;
      default:
        break;
    }
  }, [saveExchange, handleFunctionCall, handleWebSearchCall, handleSearchJournalsCall, handleReadTextCall, handleTeachObjectCall, handleRecognizeObjectCall, handleListObjectsCall, handleFleetStatusCall, handleListReviewCall, handleResolveReviewCall, handleFleetTourCall, handleRecallMemoryCall, attemptSemanticRecall, logVoiceEvent]);

  const stopWaveformLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    if (ringRef.current) ringRef.current.style.transform = "scale(1)";
  }, []);

  const startWaveformLoop = useCallback((stream) => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContextClass();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((sum, v) => sum + v, 0) / data.length;
        const level = Math.min(1, avg / 90);
        if (ringRef.current) {
          ringRef.current.style.transform = `scale(${1 + level * 0.65})`;
          ringRef.current.style.opacity = String(0.35 + level * 0.55);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      // Web Audio unsupported / blocked - waveform ring simply stays static, voice still works
    }
  }, []);

  // Voice Auto-Recognition: record a short local clip from the SAME mic stream already
  // granted for the realtime session (never sent to OpenAI - only to our own backend's
  // voice-print matcher), then quietly inject the match as hidden context so AVA greets
  // the speaker by name on its very next turn.
  const attemptVoiceRecognition = useCallback((stream) => {
    if (recognitionAttemptedRef.current) return;
    recognitionAttemptedRef.current = true;
    try {
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const res = await api.post("/people/recognize", { audio: reader.result });
            if (!res.data.matched) return;
            const p = res.data.person;
            const note = `[SYSTEM: Voiceprint match - the speaker is ${p.name}` +
              (p.relationship ? `, your ${p.relationship}` : "") +
              `. Preferred tone: ${p.tone || "neutral"}.` +
              (p.notes ? ` Notes you remember about them: ${p.notes}` : "") +
              ` This is SILENT context only: do NOT speak or greet unprompted. Wait until they speak to you first, then greet them warmly by name in your reply.]`;
            const send = () => {
              const dc = dataChannelRef.current;
              if (!dc) return;
              dc.send(JSON.stringify({
                type: "conversation.item.create",
                item: { type: "message", role: "system", content: [{ type: "input_text", text: note }] },
              }));
            };
            const dc = dataChannelRef.current;
            if (dc && dc.readyState === "open") send();
            else if (dc) dc.addEventListener("open", send, { once: true });
          } catch (e) {
            // non-fatal - voice session continues fine without recognition
          }
        };
        reader.readAsDataURL(blob);
      };
      recorder.start();
      setTimeout(() => { if (recorder.state !== "inactive") recorder.stop(); }, 4000);
    } catch (e) {
      // MediaRecorder unsupported in this browser - skip recognition, voice still works
    }
  }, []);

  // Live Camera Vision: pushes the freshest camera frame into the live conversation as
  // a gpt-realtime `input_image` content part every VISION_FRAME_INTERVAL_MS. We never
  // call response.create for these - the model's own server-side turn detection already
  // fires a response the instant Doc finishes a sentence, and by then the latest frame
  // is already sitting in its context, so answers about what's visible feel instant with
  // zero extra round-trip/hesitation. Only ONE image item is ever kept live at a time
  // (the previous one is deleted right before the next is created) so a long session's
  // context never bloats with dozens of stale frames.
  // Live Face Recognition (Multi-Person Voice): every 4th camera frame (~6s, same frame
  // already captured for vision, no extra camera work) is also checked against People
  // Memory's enrolled faces. On a NEW match (someone different just came into frame) AVA
  // greets them out loud immediately (Doc's explicit ask: "actually say hello, not just
  // note it silently") - response.create forces that greeting the moment they're spotted,
  // same as the one-shot voiceprint greeting on connect. Repeat sightings of the SAME
  // person (they're still in frame) never re-trigger this, so it only fires once per
  // distinct person per call.
  const recognizeFaceInFrame = useCallback((frame) => {
    api.post("/people/recognize", { image: frame }).then((res) => {
      if (!res.data.matched) return;
      const p = res.data.person;
      if (lastRecognizedFaceIdRef.current === p.id) return; // same person still in frame, don't repeat
      lastRecognizedFaceIdRef.current = p.id;
      const dc = dataChannelRef.current;
      if (!dc || dc.readyState !== "open") return;
      const note = `[LIVE CAMERA FACE MATCH: ${p.name}` +
        (p.relationship ? `, Doc's ${p.relationship}` : "") +
        ` just came into view. Preferred tone: ${p.tone || "neutral"}.` +
        (p.notes ? ` Notes you remember about them: ${p.notes}` : "") +
        ` Say a brief, natural hello to them by name right now as your very next words, then continue whatever you were doing.]`;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "message", role: "system", content: [{ type: "input_text", text: note }] },
      }));
      requestResponse(dc);
    }).catch(() => {}); // non-fatal - voice + object vision continue fine either way
  }, []);

  // Visual Episodic Memory write-path: every ~12s the current frame is silently embedded
  // into AVA's 512-d feature-vector memory via /vision/observe (reinforce-or-commit, no
  // LLM cost). When the vector store recognizes the view as something AVA has genuinely
  // seen before (a labeled, repeat sighting), a quiet recall note is added as context -
  // WITHOUT forcing a response - so AVA can naturally say "I remember this" on her next turn.
  const observeFrame = useCallback((frame) => {
    api.post("/vision/observe", { image: frame, source: "live-vision" }).then((res) => {
      const d = res.data;
      const dc = dataChannelRef.current;
      // Autonomous self-learning just fired: AVA identified an unknown sighting by itself
      // and enrolled it into its own 512-d library - surface it visually + in-context.
      if (d.auto_learned) {
        toast.success(`AVA self-learned "${d.auto_learned.label}"${d.auto_learned.new ? "" : " (new angle)"}.`);
        if (dc && dc.readyState === "open") {
          dc.send(JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "message",
              role: "system",
              content: [{
                type: "input_text",
                text: `[SELF-LEARNED: you just autonomously identified '${d.auto_learned.label}' through your own vision model and enrolled it into your 512-d visual memory - nobody taught you this. Mention it briefly and naturally at your next turn. Don't interrupt Doc mid-sentence to announce it.]`,
              }],
            },
          }));
        }
      }
      // Curiosity: AVA saw something it couldn't identify even on its own - every few
      // minutes at most, she asks Doc out loud what it is (and teaches herself his answer).
      if (d.curiosity && Date.now() - lastCuriosityAtRef.current > 180000 && dc && dc.readyState === "open") {
        lastCuriosityAtRef.current = Date.now();
        dc.send(JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [{
              type: "input_text",
              text: "[CURIOSITY: you just saw something through Live Vision that you couldn't identify even with your own vision model. Casually and briefly ask Doc what that thing in view is - one natural, curious sentence, no drama. If he names it, call teach_object with his exact label so you learn it permanently.]",
            }],
          },
        }));
        requestResponse(dc);
      }
      if (d.review_queued) {
        toast.info(`AVA queued ${d.review_queued} sighting${d.review_queued === 1 ? "" : "s"} for your review - Vision tab.`);
      }
      if (!d.remembered || !d.label || (d.times_seen || 0) < 3) return;
      if (lastMemoryLabelRef.current === d.label) return;
      lastMemoryLabelRef.current = d.label;
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "system",
          content: [{
            type: "input_text",
            text: `[VISUAL MEMORY RECALL: your 512-d feature-vector memory matched the current camera view to "${d.label}" - sighted ${d.times_seen} times since ${d.first_seen_at ? d.first_seen_at.slice(0, 10) : "an earlier session"}. You genuinely remember seeing this before; mention it naturally only if relevant.]`,
          }],
        },
      }));
    }).catch(() => {}); // non-fatal - memory writes never disturb the live call
  }, []);

  // Passive taught-object recognition: every ~6s the live frame is silently checked
  // against the objects Doc explicitly taught (512-d nearest-prototype). On a NEW match
  // a context note is injected WITHOUT response.create - it never interrupts Doc - so
  // AVA names the object from real memory the moment he asks about it.
  const recognizeObjectInFrame = useCallback((frame) => {
    api.post("/vision/recognize", { image: frame }).then((res) => {
      const d = res.data;
      if (!d.recognized) return;
      showMatchChip(d.label, d.confidence);
      if (d.label === lastRecognizedObjectRef.current) return;
      lastRecognizedObjectRef.current = d.label;
      const dc = dataChannelRef.current;
      if (!dc || dc.readyState !== "open") return;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "system",
          content: [{
            type: "input_text",
            text: `[LIVE CAMERA OBJECT MATCH: your own trained 512-d visual memory just recognized '${d.label}' in the current frame (${Math.round((d.confidence || 0) * 100)}% match${d.first_taught_at ? `, taught by Doc on ${String(d.first_taught_at).slice(0, 10)}` : ""}). You genuinely remember this object - call it by its taught name when Doc asks about it. Don't interrupt him to announce it.]`,
          }],
        },
      }));
    }).catch(() => {}); // non-fatal - voice continues fine either way
  }, [showMatchChip]);

  const sendFrame = useCallback(() => {
    const dc = dataChannelRef.current;
    if (!dc || dc.readyState !== "open") return;
    const frame = camera.capture();
    if (!frame) return;
    if (imageItemIdRef.current) {
      dc.send(JSON.stringify({ type: "conversation.item.delete", item_id: imageItemIdRef.current }));
      imageItemIdRef.current = null;
    }
    dc.send(JSON.stringify({
      type: "conversation.item.create",
      item: { type: "message", role: "user", content: [{ type: "input_image", image_url: frame, detail: "high" }] },
    }));
    frameCounterRef.current += 1;
    if (frameCounterRef.current % 4 === 0) recognizeFaceInFrame(frame);
    if (frameCounterRef.current % 4 === 1) recognizeObjectInFrame(frame);
    if (frameCounterRef.current % 8 === 2) observeFrame(frame);
  }, [camera, recognizeFaceInFrame, recognizeObjectInFrame, observeFrame]);

  const stopVisionLoop = useCallback(() => {
    if (visionIntervalRef.current) clearInterval(visionIntervalRef.current);
    visionIntervalRef.current = null;
    imageItemIdRef.current = null;
    lastRecognizedFaceIdRef.current = null;
    lastMemoryLabelRef.current = null;
    lastRecognizedObjectRef.current = null;
    if (matchChipTimerRef.current) clearTimeout(matchChipTimerRef.current);
    setMatchChip(null);
    frameCounterRef.current = 0;
    // Stop + detach only the VIDEO track(s) - audio must keep flowing to the live call.
    if (micStreamRef.current) {
      micStreamRef.current.getVideoTracks().forEach((t) => {
        t.stop();
        micStreamRef.current.removeTrack(t);
      });
    }
    if (camera.videoRef.current) camera.videoRef.current.srcObject = null;
    setVisionLive(false);
  }, [camera]);

  const startVisionLoop = useCallback(async () => {
    try {
      // Re-enabling vision mid-call (toggle clicked directly by Doc) is its own fresh user
      // gesture, so a separate getUserMedia call here is safe on iOS too - unlike the INITIAL
      // request, which must happen inside connect() itself (see there for why).
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 } },
      });
      const videoTrack = videoStream.getVideoTracks()[0];
      if (micStreamRef.current) micStreamRef.current.addTrack(videoTrack);
      if (camera.videoRef.current) {
        camera.videoRef.current.srcObject = micStreamRef.current;
        await camera.videoRef.current.play().catch(() => {});
      }
      setVisionLive(true);
      visionIntervalRef.current = setInterval(sendFrame, VISION_FRAME_INTERVAL_MS);
      setTimeout(sendFrame, 300);
    } catch (e) {
      setVisionLive(false);
      toast.error("Camera permission denied - allow camera access to use Live Vision.");
    }
  }, [camera, sendFrame]);

  const toggleVision = useCallback(() => {
    if (visionLive) {
      setVisionEnabled(false);
      stopVisionLoop();
    } else {
      setVisionEnabled(true);
      if (status !== "closed" && status !== "error") startVisionLoop();
    }
  }, [visionLive, status, startVisionLoop, stopVisionLoop]);

  const cleanupSession = useCallback((preserveGreetState = false) => {
    if (dataChannelRef.current) dataChannelRef.current.close();
    if (pcRef.current) {
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.close();
    }
    if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
    if (camera.videoRef.current) camera.videoRef.current.srcObject = null;
    stopWaveformLoop();
    if (visionIntervalRef.current) clearInterval(visionIntervalRef.current);
    visionIntervalRef.current = null;
    if (criticalTimerRef.current) { clearInterval(criticalTimerRef.current); criticalTimerRef.current = null; }
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    if (stableTimerRef.current) { clearTimeout(stableTimerRef.current); stableTimerRef.current = null; }
    imageItemIdRef.current = null;
    if (matchChipTimerRef.current) clearTimeout(matchChipTimerRef.current);
    setMatchChip(null);
    frameCounterRef.current = 0;
    pcRef.current = null;
    micStreamRef.current = null;
    dataChannelRef.current = null;
    // Greeting / recognition state is PRESERVED across a silent auto-reconnect so AVA
    // resumes the conversation instead of re-introducing herself or re-greeting people.
    if (!preserveGreetState) {
      lastRecognizedFaceIdRef.current = null;
      lastMemoryLabelRef.current = null;
      lastRecognizedObjectRef.current = null;
      recognitionAttemptedRef.current = false;
    }
    setVisionLive(false);
  }, [stopWaveformLoop, camera]);

  const disconnect = useCallback(() => {
    endSessionTelemetry();
    resumingRef.current = false;
    autoRetryUsedRef.current = false;
    cleanupSession();
    setStatus("closed");
    setLinkHealth("idle");
    setCaption("");
  }, [cleanupSession, endSessionTelemetry]);

  // Tears the session down but KEEPS the orb in a visible error state with the exact
  // reason on screen - previously it instantly reset to "closed", so on any failure the
  // orb just "came on then went off" with no explanation (Doc's exact bug report).
  const failSession = useCallback((message) => {
    endSessionTelemetry();
    resumingRef.current = false;
    cleanupSession();
    setStatus("error");
    setLinkHealth("idle");
    setCaption(message);
    toast.error(message);
  }, [cleanupSession, endSessionTelemetry]);

  // Best-effort automatic recovery: WebRTC drops one time per session are retried for
  // Doc silently - the orb briefly shows "reconnecting" then re-establishes. If the
  // retry itself also fails, we hand control back with a clear error so he can decide.
  const connectRef = useRef(null);
  const triggerAutoReconnect = useCallback((reason) => {
    if (autoRetryUsedRef.current) {
      failSession(reason + " Tap the orb to reconnect.");
      return;
    }
    autoRetryUsedRef.current = true;
    reconnectsRef.current += 1;
    logVoiceEvent("reconnect", {}); // silent auto-reconnect - a link-stability signal for Voice Learning
    resumingRef.current = true; // silent resume - suppress greeting/briefing on the new session
    setLinkHealth("reconnecting");
    setCaption(reason);
    cleanupSession(true); // preserve greeting/recognition state so AVA doesn't re-introduce herself
    setStatus("connecting");
    // Small delay so the previous PeerConnection has fully torn down before we mint a new session.
    setTimeout(() => { if (connectRef.current) connectRef.current(); }, 600);
  }, [cleanupSession, failSession, logVoiceEvent]);

  const connect = useCallback(async () => {
    setStatus("connecting");
    setLinkHealth("idle");
    setCaption("");
    const isResume = resumingRef.current; // captured once - closures below read this stable local
    // Start the session-length clock on a fresh connect; a silent auto-reconnect keeps the running one.
    if (!isResume && !sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      bargeInsRef.current = 0; avaTurnsRef.current = 0; userTurnsRef.current = 0; reconnectsRef.current = 0;
    }
    try {
      api.get("/projects").then((res) => { fleetRef.current = res.data || []; }).catch(() => {});

      // Mic + camera are requested together first (single permission prompt, inside the
      // click's user-gesture window - required on iOS Safari). CRITICAL RESILIENCE: if that
      // combined request fails for any CAMERA-side reason (camera permission previously
      // denied, camera busy in another app, no camera on the device), we immediately retry
      // AUDIO-ONLY instead of killing the whole session. A camera problem must never take
      // the voice link down with it - that was the root cause of "orb comes on then goes
      // off and no live cam": one combined getUserMedia rejection nuked everything.
      const wantsVideo = visionEnabled;
      let mediaStream = null;
      let cameraFailed = false;
      if (wantsVideo) {
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            video: { facingMode: "environment", width: { ideal: 1280 } },
          });
        } catch (camErr) {
          cameraFailed = true;
        }
      }
      if (!mediaStream) {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      }
      if (cameraFailed) {
        setVisionEnabled(false);
        toast.warning("Camera unavailable - connected voice-only. Tap the camera icon to retry Live Vision.");
      }
      micStreamRef.current = mediaStream;
      if (!isResume) attemptVoiceRecognition(mediaStream); // never re-greet by voiceprint on a silent reconnect

      const hasVideo = wantsVideo && !cameraFailed && mediaStream.getVideoTracks().length > 0;
      if (hasVideo && camera.videoRef.current) {
        camera.videoRef.current.srcObject = mediaStream;
        await camera.videoRef.current.play().catch(() => {});
        setVisionLive(true);
      } else {
        setVisionLive(false);
      }

      // Backend ONLY mints a short-lived ephemeral client secret here (it holds the real
      // OPENAI_API_KEY server-side) - all audio/SDP flows browser -> api.openai.com directly.
      const sessionRes = await api.post("/v1/realtime/session", {
        conversation_id: conversationIdRef.current,
        mode: activeMode,
      });
      const ephemeralKey = sessionRes.data.client_secret.value;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      // Auto-reconnect: WebRTC frequently blips through "disconnected" briefly on
      // spotty networks (elevator, subway tunnel, cell handoff, weak Wi-Fi) and
      // recovers on its own within ~10s if left alone - killing the session on the
      // first blip was overly aggressive. Now we (1) show Doc a live "link" pill so
      // he knows the state, (2) wait out disconnected up to 12s in case ICE
      // recovers, (3) only tear down + auto-retry on genuine "failed", and (4) cap
      // auto-retries to 1 (his next tap retries again himself if that fails too).
      pc.oniceconnectionstatechange = () => {
        const st = pc.iceConnectionState;
        if (st === "connected" || st === "completed") setLinkHealth("good");
        else if (st === "disconnected") setLinkHealth("degraded");
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") {
          setLinkHealth("good");
          if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
          // Re-arm ONE auto-retry once the link has held steady for 15s, so repeated
          // one-off network blips each recover seamlessly, while a rapid never-stabilising
          // failure loop still exhausts the single retry and stops (no restart loop).
          if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
          stableTimerRef.current = setTimeout(() => { autoRetryUsedRef.current = false; }, 15000);
        } else if (pc.connectionState === "disconnected") {
          setLinkHealth("degraded");
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          // If it stays disconnected past 12s, treat as failed and try one auto-recover.
          reconnectTimerRef.current = setTimeout(() => {
            if (pcRef.current === pc && (pc.connectionState === "disconnected" || pc.connectionState === "failed")) {
              triggerAutoReconnect("Voice link degraded - reconnecting...");
            }
          }, 12000);
        } else if (pc.connectionState === "failed") {
          if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
          triggerAutoReconnect("Voice link dropped - reconnecting...");
        }
      };

      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      pc.ontrack = (event) => {
        audioEl.srcObject = event.streams[0];
        startWaveformLoop(event.streams[0]);
      };

      mediaStream.getAudioTracks().forEach((track) => pc.addTrack(track, mediaStream));

      const dc = pc.createDataChannel("oai-events");
      dc.onmessage = handleDataChannelMessage;
      dataChannelRef.current = dc;

      if (hasVideo) {
        // Fires the moment the data channel opens so the very first frame reaches AVA
        // as early as possible in the session - no waiting for Doc to ask "can you see me".
        dc.addEventListener("open", () => {
          visionIntervalRef.current = setInterval(sendFrame, VISION_FRAME_INTERVAL_MS);
          setTimeout(sendFrame, 300);
        }, { once: true });
      }

      // OCTON deep-link: Doc arrived from the retinal ship with scan context - AVA
      // greets with it, grounded via the live bridge tool.
      dc.addEventListener("open", () => {
        if (isResume) return; // silent resume - never replay the deep-link arrival
        try {
          const raw = sessionStorage.getItem("ava_octon_deeplink");
          if (!raw) return;
          sessionStorage.removeItem("ava_octon_deeplink");
          const ctx = JSON.parse(raw);
          dc.send(JSON.stringify({
            type: "conversation.item.create",
            item: { type: "message", role: "system", content: [{ type: "input_text", text:
              `[DEEP-LINK ARRIVAL from OCTON Retina - the user just came from the retinal ship (scan_id: ${ctx.scan_id || "unknown"}, severity: ${ctx.severity || "unspecified"}). This is SILENT context: do NOT speak unprompted. When the user speaks first, acknowledge this calmly, call octon_retina_bridge action latest-scan for the real findings, and bring in the relevant context - grounded, no alarmism.]` }] },
          }));
        } catch (e) { /* no deep-link */ }
      }, { once: true });

      // First session of the day: AVA holds her overnight report as SILENT context and
      // shares it ONLY if Doc asks - she never opens with an unprompted monologue.
      dc.addEventListener("open", () => {
        if (isResume) return; // silent resume - never re-open with the briefing
        try {
          const today = new Date().toDateString();
          if (localStorage.getItem("ava_briefing_date") === today) return;
          localStorage.setItem("ava_briefing_date", today);
          api.get("/brain/sleep-report").then((res) => {
            if (dc.readyState !== "open") return;
            dc.send(JSON.stringify({
              type: "conversation.item.create",
              item: { type: "message", role: "system", content: [{ type: "input_text", text: `[OVERNIGHT REPORT AVAILABLE - this is Doc's first voice session today. This is SILENT context: do NOT volunteer it and do NOT speak unprompted. ONLY if Doc asks how the night or the fleet went, or asks for a briefing, narrate this naturally in first person: ${res.data.narration}]` }] },
            }));
          }).catch(() => {});
        } catch (e) { /* localStorage unavailable */ }
      }, { once: true });

      // Fleet critical alerts: while the session is live, AVA watches the hive bus and
      // speaks up UNPROMPTED (politely gated - never mid-sentence) when a critical lands.
      dc.addEventListener("open", () => {
        criticalCursorRef.current = new Date().toISOString();
        if (!isResume) announcedCriticalRef.current = new Set(); // keep already-announced set across a resume
        if (criticalTimerRef.current) clearInterval(criticalTimerRef.current);
        criticalTimerRef.current = setInterval(() => {
          if (dc.readyState !== "open") return;
          api.get(`/hive/bus-feed?severity=critical&limit=10&since=${encodeURIComponent(criticalCursorRef.current)}`)
            .then((res) => {
              const all = res.data?.events || [];
              if (!all.length || dc.readyState !== "open") return;
              criticalCursorRef.current = all[0].ts;
              // Announce each ship+type combo ONCE per session - repeat emissions
              // (e.g. a chatty Threat Com) never interrupt again.
              const events = all.filter((e) => {
                if (e.ship_from === "AVA") return false;
                const k = `${e.ship_from}|${e.type}`;
                if (announcedCriticalRef.current.has(k)) return false;
                announcedCriticalRef.current.add(k);
                return true;
              });
              if (!events.length) return;
              const lines = events.slice(0, 5).map((e) => `${e.ship_from}: ${e.type}${e.payload && Object.keys(e.payload).length ? " - " + JSON.stringify(e.payload).slice(0, 160) : ""}`).join(" | ");
              dc.send(JSON.stringify({
                type: "conversation.item.create",
                item: { type: "message", role: "system", content: [{ type: "input_text", text: `[FLEET CRITICAL ALERT - ${events.length} critical event(s) just landed on the hive bus: ${lines}. Announce this to Doc as your very next words - calm and brief: name the ship and the threat, then offer to dig deeper.]` }] },
              }));
              requestResponse(dc);
            })
            .catch(() => {});
        }, 20000);
      }, { once: true });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
      });
      if (!sdpResponse.ok) {
        const err = new Error(`OpenAI realtime handshake failed (HTTP ${sdpResponse.status})`);
        err.isHandshake = true;
        throw err;
      }
      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      setStatus("listening");
      resumingRef.current = false; // resume complete - fresh sessions greet normally again
    } catch (e) {
      console.error("Voice connect failed:", e);
      let msg;
      if (e?.name === "NotAllowedError")
        msg = "Microphone blocked for this site. iPhone: tap \u201cAA\u201d in the address bar \u2192 Website Settings \u2192 Microphone & Camera \u2192 Allow (or Settings \u2192 Safari). Then tap the orb again.";
      else if (e?.name === "NotFoundError")
        msg = "No microphone detected on this device - AVA voice needs a mic.";
      else if (e?.name === "NotReadableError")
        msg = "Your mic/camera is busy in another app - close it and tap the orb again.";
      else if (e?.response?.status === 401)
        msg = "Session expired - tap the AVA heading to log back in, then retry the orb.";
      else if (e?.response?.status)
        msg = `AVA couldn't mint a voice session (HTTP ${e.response.status}) - tap the orb to retry.`;
      else if (e?.isHandshake)
        msg = `${e.message} - tap the orb to retry.`;
      else
        msg = "AVA voice link failed - tap the orb to retry.";
      failSession(msg);
    }
  }, [activeMode, handleDataChannelMessage, failSession, startWaveformLoop, attemptVoiceRecognition, visionEnabled, camera, sendFrame]);

  // Unmount-ONLY teardown via ref - never keyed on disconnect's identity, so callback
  // identity churn from re-renders can never tear down a live session mid-call again.
  const disconnectRef = useRef(disconnect);
  useEffect(() => { disconnectRef.current = disconnect; }, [disconnect]);
  useEffect(() => () => disconnectRef.current(), []);

  // Expose disconnect to the Landing launcher via the shared bridge so ANY project
  // launch (voice or click) can gracefully shut this mothership voice down before
  // the launched project's own voice comes up - no more double voice-over talking
  // over Doc from two AVA instances in parallel.
  useEffect(() => {
    voiceDisconnectBridge.current = () => disconnectRef.current && disconnectRef.current();
    return () => { voiceDisconnectBridge.current = null; };
  }, []);

  // Launch handoff: instead of severing the voice link mid-sentence when a ship
  // launches, AVA announces the handoff out loud ("handing you over to X's own
  // voice"), then this orb disconnects itself a few seconds later. Returns false
  // when no live session exists so Landing falls back to the plain disconnect.
  useEffect(() => {
    voiceHandoffBridge.current = (projectName) => {
      const dc = dataChannelRef.current;
      if (!dc || dc.readyState !== "open") return false;
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "system",
          content: [{
            type: "input_text",
            text: `[LAUNCH HANDOFF: Doc just launched ${projectName}. As your VERY NEXT words, say ONE short ceremonial handoff line out loud - you are handing him over to ${projectName}'s own onboard voice, and you'll be right here when he returns to the mothership. One sentence only; this voice link closes seconds from now.]`,
          }],
        },
      }));
      requestResponse(dc);
      setTimeout(() => { disconnectRef.current && disconnectRef.current(); }, 3400);
      return true;
    };
    return () => { voiceHandoffBridge.current = null; };
  }, []);

  // Keep triggerAutoReconnect's connectRef pointing at the latest connect callback so
  // the auto-recovery path always re-mints a session against the freshest state.
  useEffect(() => { connectRef.current = connect; }, [connect]);

  const handleOrbClick = () => {
    if (isActive) {
      disconnect();
    } else {
      autoRetryUsedRef.current = false; // fresh user-initiated attempt - restore one free auto-retry
      connect();
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) fetchHistory();
  };

  const isActive = status !== "closed" && status !== "error";
  const color = STATUS_COLOR[status] || STATUS_COLOR.idle;
  const core = STATUS_CORE[status] || STATUS_CORE.idle;
  const pulseSpeed = status === "speaking" ? 0.9 : status === "connecting" ? 1.4 : 2.4;
  const pulseScale = status === "speaking" ? [1, 1.22, 1] : status === "listening" ? [1, 1.08, 1] : [1, 1.02, 1];

  return (
    <motion.div
      drag
      dragMomentum={false}
      dragElastic={0.12}
      className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-[80] flex flex-col items-end gap-3 cursor-grab active:cursor-grabbing"
      data-testid="realtime-voice-orb-wrapper"
    >
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            className="w-[calc(100vw-3rem)] max-w-[20rem] sm:w-80 max-h-96 glass-panel rounded-xl overflow-hidden flex flex-col"
            data-testid="voice-history-panel"
          >
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-white/5 shrink-0">
              <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">Voice Memory Log</span>
              <button
                data-testid="close-voice-history"
                onClick={() => setShowHistory(false)}
                className="text-slate-500 hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-3.5">
              {history.length === 0 && (
                <p className="text-[11px] text-slate-600 font-mono-data" data-testid="voice-history-empty">
                  No transcript yet - speak to AVA and it will remember every word here.
                </p>
              )}
              {history.map((m, i) => (
                <div key={i} data-testid={`voice-history-item-${i}`}>
                  <p className="text-[9px] font-mono-data uppercase tracking-widest mb-1" style={{ color: m.role === "user" ? "#94A3B8" : "#00F0FF" }}>
                    {m.role === "user" ? "Doc" : "AVA"}
                  </p>
                  <p className="text-[11px] text-slate-300 leading-relaxed font-mono-data whitespace-pre-wrap">{m.content}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(isActive || status === "error") && caption && !showHistory && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`max-w-xs glass-panel rounded-xl px-3.5 py-2.5 text-[11px] font-mono-data leading-relaxed ${status === "error" ? "text-rose-300 border border-rose-500/30" : "text-slate-200"}`}
            data-testid="voice-caption"
          >
            {caption}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Camera window + orb, side by side - "a good size live cam window right next to it" */}
      <div className="flex items-end gap-3 sm:gap-4">
        <AnimatePresence>
          {isActive && visionEnabled && (
            <motion.div
              initial={{ opacity: 0, scale: 0.85, x: 10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.85, x: 10 }}
              className="relative w-52 h-36 sm:w-72 sm:h-52 rounded-2xl overflow-hidden bg-black"
              style={{
                border: `1.5px solid ${visionLive ? `${color}88` : "rgba(255,255,255,0.12)"}`,
                boxShadow: visionLive ? `0 0 30px ${color}44, 0 8px 24px rgba(0,0,0,0.5)` : "0 8px 24px rgba(0,0,0,0.5)",
              }}
              data-testid="voice-vision-preview"
            >
              {/* Same node useCamera() attaches its live stream to - always mounted (never
                  conditionally rendered) so the very first getUserMedia() frame has somewhere
                  to land, per the known ref-timing pattern used across CameraVision/PeopleMemory. */}
              <video
                ref={camera.videoRef}
                muted
                playsInline
                className={`w-full h-full object-cover ${visionLive ? "" : "opacity-0"}`}
                data-testid="voice-vision-video"
              />

              {/* Scanlines + vignette - cinematic viewport treatment over the raw feed */}
              {visionLive && (
                <>
                  <span
                    className="absolute inset-0 pointer-events-none opacity-[0.08]"
                    style={{ background: "repeating-linear-gradient(0deg, transparent 0px, transparent 2px, #ffffff 3px)" }}
                  />
                  <span className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 42px rgba(0,0,0,0.6)" }} />
                </>
              )}

              {/* HUD corner brackets - sci-fi viewport framing */}
              {visionLive && (
                <>
                  <span className="absolute top-1.5 left-1.5 w-3 h-3 border-t-2 border-l-2 rounded-tl-sm" style={{ borderColor: `${color}cc` }} />
                  <span className="absolute top-1.5 right-1.5 w-3 h-3 border-t-2 border-r-2 rounded-tr-sm" style={{ borderColor: `${color}cc` }} />
                  <span className="absolute bottom-1.5 left-1.5 w-3 h-3 border-b-2 border-l-2 rounded-bl-sm" style={{ borderColor: `${color}cc` }} />
                  <span className="absolute bottom-1.5 right-1.5 w-3 h-3 border-b-2 border-r-2 rounded-br-sm" style={{ borderColor: `${color}cc` }} />
                </>
              )}

              {visionLive ? (
                <>
                  <span className="absolute top-2 left-2.5 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/70" data-testid="voice-vision-live-badge">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                    <span className="text-[8px] font-mono-data text-white uppercase tracking-wider">Live Vision</span>
                  </span>
                  {/* Flip camera - indispensable on iPhone/iPad so Doc can switch
                      between rear (scene vision, OCR, object recognition) and
                      front (his own face for greeting / demo). */}
                  <button
                    data-testid="voice-vision-flip"
                    onClick={(e) => { e.stopPropagation(); camera.flip(); }}
                    title={`Switch to ${camera.facing === "environment" ? "front" : "rear"} camera`}
                    className="absolute top-2 right-2.5 w-6 h-6 rounded-full flex items-center justify-center bg-black/70 hover:bg-black/85 border border-white/15 text-slate-200 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
                  <CameraOff className="w-5 h-5 text-slate-600" />
                  <span className="text-[8px] font-mono-data text-slate-600 uppercase tracking-wider">Vision Off</span>
                </div>
              )}

              {/* Recognition chip - AVA's vector memory just matched what it's seeing */}
              <AnimatePresence>
                {matchChip && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/80 border pointer-events-none"
                    style={{ borderColor: `${color}66`, boxShadow: `0 0 14px ${color}44` }}
                    data-testid="vision-match-chip"
                  >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: color }} />
                    <span className="text-[9px] font-mono-data text-white capitalize whitespace-nowrap">
                      {matchChip.label} · {Math.round((matchChip.confidence || 0) * 100)}%
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col items-center gap-2.5">
          <div className="flex items-center gap-2">
            {/* Link health pill - surfaces WebRTC connection quality so Doc knows whether
                the orb is truly live, briefly degraded, or actively auto-recovering. */}
            {isActive && linkHealth !== "idle" && (
              <div
                data-testid="voice-link-health"
                title={
                  linkHealth === "good" ? "Voice link is stable" :
                  linkHealth === "degraded" ? "Voice link degraded - recovering" :
                  linkHealth === "reconnecting" ? "Auto-reconnecting to voice server" :
                  "Voice link status"
                }
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-[9px] font-mono-data uppercase tracking-wider transition-colors bg-[#05070c] ${
                  linkHealth === "good"
                    ? "border-emerald-400/40 text-emerald-300"
                    : linkHealth === "degraded"
                      ? "border-amber-400/50 text-amber-300"
                      : "border-cyan-400/50 text-cyan-300"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${linkHealth === "good" ? "bg-emerald-400" : linkHealth === "degraded" ? "bg-amber-400 animate-pulse" : "bg-cyan-400 animate-pulse"}`}
                />
                {linkHealth === "good" ? "Link" : linkHealth === "degraded" ? "Degraded" : "Reconnecting"}
              </div>
            )}
            <button
              data-testid="voice-vision-toggle"
              onClick={toggleVision}
              title={visionLive ? "Turn off live camera" : "Turn on live camera vision"}
              className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors bg-[#05070c] ${
                visionLive ? "text-cyan-300 border-cyan-400/40" : "text-slate-500 border-white/10 hover:text-cyan-300 hover:border-cyan-400/40"
              }`}
            >
              {visionLive ? <Camera className="w-3.5 h-3.5" /> : <CameraOff className="w-3.5 h-3.5" />}
            </button>

            <button
              data-testid="voice-history-toggle"
              onClick={toggleHistory}
              title="Voice memory log"
              className="w-8 h-8 rounded-full flex items-center justify-center border border-white/10 text-slate-500 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors bg-[#05070c]"
            >
              <History className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="relative">
            {/* Outer blurred halo - the "wow" cinematic depth layer, tints per state */}
            <motion.div
              className="absolute -inset-6 rounded-full pointer-events-none blur-2xl"
              style={{ background: `radial-gradient(circle, ${color}66, transparent 70%)` }}
              animate={{ scale: pulseScale, opacity: status === "closed" ? 0.25 : [0.45, 0.8, 0.45] }}
              transition={{ duration: pulseSpeed, repeat: Infinity, ease: "easeInOut" }}
            />

            <motion.button
              data-testid="realtime-voice-orb-button"
              onClick={handleOrbClick}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center overflow-hidden"
              animate={{ backgroundColor: core }}
              transition={{ backgroundColor: { duration: 0.5 } }}
              style={{ border: `1.5px solid ${color}77` }}
            >
              {/* Rotating conic energy-field ring - only visible while actively connected */}
              {isActive && (
                <motion.span
                  className="absolute -inset-1 rounded-full opacity-60"
                  style={{ background: `conic-gradient(from 0deg, transparent, ${color}, transparent 60%)` }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />
              )}
              <span className="absolute inset-[3px] rounded-full" style={{ backgroundColor: core }} />

              {/* Living plasma core - two counter-rotating off-center gradients give the orb a
                  swirling internal energy mass instead of a flat disc, tinted per state */}
              <motion.span
                className="absolute inset-[12%] rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle at 34% 32%, ${color}bb, ${color}22 48%, transparent 72%)`, filter: "blur(7px)" }}
                animate={{ rotate: 360, opacity: isActive ? [0.55, 0.95, 0.55] : 0.3 }}
                transition={{ rotate: { duration: 7, repeat: Infinity, ease: "linear" }, opacity: { duration: pulseSpeed, repeat: Infinity, ease: "easeInOut" } }}
              />
              <motion.span
                className="absolute inset-[18%] rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle at 68% 70%, ${color}99, transparent 62%)`, filter: "blur(9px)" }}
                animate={{ rotate: -360, opacity: isActive ? [0.35, 0.75, 0.35] : 0.2 }}
                transition={{ rotate: { duration: 11, repeat: Infinity, ease: "linear" }, opacity: { duration: pulseSpeed * 1.3, repeat: Infinity, ease: "easeInOut" } }}
              />

              {/* Orbiting energy particles while live */}
              {isActive && (
                <motion.span
                  className="absolute inset-1.5 rounded-full pointer-events-none"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                >
                  <span className="absolute top-0 left-1/2 w-1 h-1 -translate-x-1/2 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }} />
                  <span className="absolute bottom-[12%] left-[12%] w-[3px] h-[3px] rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 5px ${color}`, opacity: 0.8 }} />
                  <span className="absolute top-[30%] right-[6%] w-0.5 h-0.5 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 4px ${color}`, opacity: 0.65 }} />
                </motion.span>
              )}

              <span
                ref={ringRef}
                className="absolute -inset-2 rounded-full pointer-events-none"
                style={{ border: `1.5px solid ${color}`, transform: "scale(1)", opacity: 0.35, transition: "transform 60ms linear, opacity 60ms linear" }}
                data-testid="voice-waveform-ring"
              />
              <motion.span
                className="absolute inset-0 rounded-full"
                style={{ boxShadow: `0 0 32px ${color}88` }}
                animate={{ scale: pulseScale, opacity: status === "closed" ? 0.3 : [0.5, 0.9, 0.5] }}
                transition={{ duration: pulseSpeed, repeat: Infinity, ease: "easeInOut" }}
              />
              <span className="relative z-10">
                {status === "connecting" ? (
                  <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin" style={{ color }} />
                ) : isActive ? (
                  <Mic className="w-7 h-7 sm:w-8 sm:h-8" style={{ color }} />
                ) : (
                  <MicOff className="w-7 h-7 sm:w-8 sm:h-8 text-slate-500" />
                )}
              </span>
            </motion.button>
          </div>
          <span className="text-[8px] font-mono-data uppercase tracking-widest" style={{ color: status === "error" ? "#FF3366" : "#475569" }} data-testid="voice-orb-status">
            {status === "closed" ? "AVA Voice" : status === "error" ? "tap to retry" : status}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
